import React, { useState, useEffect } from 'react';
import { CreditCard, Loader, CheckCircle, AlertCircle, Shield, Lock } from 'lucide-react';
import { load } from "@cashfreepayments/cashfree-js";
import './CashfreePayment.css';
import { constructApiUrl } from '../../utils/apiUtils';

const CashfreePayment = ({ 
  amount, 
  currency = 'INR', 
  orderId,
  customerInfo,
  onSuccess,
  onError,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cashfree, setCashfree] = useState(null);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Initialize Cashfree SDK
  useEffect(() => {
    const initializeCashfree = async () => {
      try {
        const cashfreeInstance = await load({
          mode: "sandbox", // Use sandbox for test mode
        });
        setCashfree(cashfreeInstance);
      } catch (error) {
        console.error("Failed to initialize Cashfree:", error);
      }
    };

    initializeCashfree();
  }, []);

  const createPayment = async () => {
    try {
      // Cashfree expects amount in rupees, not paise
      const amountInRupees = parseFloat(amount);
      
      console.log('💰 Amount conversion:', {
        originalAmount: amount,
        amountInRupees: amountInRupees,
        currency: currency
      });

      const response = await fetch(constructApiUrl('payments/cashfree/create-order'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amountInRupees, // Amount in rupees (not paise)
          currency,
          orderId,
          customerInfo,
          appId: 'TEST365161bf1cdf14bebbe29b06de161563', // Use test app ID
          testMode: true
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      if (!data?.success) {
        throw new Error(data.message || 'Failed to create payment order');
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  const verifyPayment = async (orderID) => {
    try {
      const response = await fetch(constructApiUrl('payments/cashfree/verify'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderID }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('🔄 Starting Cashfree payment process...');
      console.log('💰 Amount:', amount);
      console.log('🆔 Order ID:', orderId);
      console.log('👤 Customer Info:', customerInfo);

      // Create payment order
      const data = await createPayment();
      console.log('✅ Payment order created:', data);

      if (!cashfree) {
        throw new Error('Cashfree SDK not initialized');
      }

      // Ensure modal is properly positioned and visible
      const ensureModalVisibility = () => {
        // Scroll to top smoothly
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Prevent body scrolling
        document.body.classList.add('modal-open');
        
        // Add CSS to ensure modal is properly positioned
        const style = document.createElement('style');
        style.id = 'cashfree-modal-style';
        style.textContent = `
          .cashfree-modal {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            z-index: 9999 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            background: rgba(0, 0, 0, 0.5) !important;
          }
          .cashfree-modal iframe {
            max-width: 90vw !important;
            max-height: 90vh !important;
            border-radius: 8px !important;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
          }
          @media (max-width: 768px) {
            .cashfree-modal iframe {
              max-width: 95vw !important;
              max-height: 95vh !important;
            }
          }
        `;
        document.head.appendChild(style);
      };

      // Apply modal positioning
      ensureModalVisibility();
      
      // Add a small delay to ensure positioning is applied
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Open Cashfree payment modal
      await cashfree.checkout({
        paymentSessionId: data.data.paymentSessionId,
        redirectTarget: "_modal",
      });
      
      // Clean up after modal closes
      setTimeout(() => {
        document.body.classList.remove('modal-open');
        const style = document.getElementById('cashfree-modal-style');
        if (style) {
          style.remove();
        }
      }, 2000);

      // Since Cashfree SDK doesn't return a result, we need to handle this differently
      // We'll verify the payment and handle different scenarios
      console.log('🔄 Cashfree modal closed, verifying payment status...');
      
      // Add a small delay to allow for any pending operations
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Verify payment to get actual status
      const verifyData = await verifyPayment(data.data.orderNumber);
      console.log('✅ Payment verification:', verifyData);

      if (verifyData.success) {
        console.log('✅ Payment successful!');
        // Pass the verification data with actual payment status from backend
        const successData = {
          ...verifyData,
          paymentStatus: verifyData.data?.status || 'completed',
          orderStatus: 'processing'
        };
        if (onSuccess) {
          onSuccess(successData);
        }
      } else {
        console.log('❌ Payment failed, cancelled, or processing');
        
        // Check if this was a cancellation
        if (verifyData.message && verifyData.message.includes('cancelled')) {
          console.log('🚫 Payment was cancelled by user');
          const cancelledData = {
            ...verifyData,
            paymentStatus: 'cancelled',
            orderStatus: 'cancelled'
          };
          if (onCancel) {
            onCancel(cancelledData);
          }
        } 
        // Check if payment was cancelled/left (from backend status)
        else if (verifyData.data && verifyData.data.status === 'cancelled') {
          console.log('🚫 Payment was cancelled/left by user');
          const cancelledData = {
            ...verifyData,
            paymentStatus: 'cancelled',
            orderStatus: 'cancelled'
          };
          if (onCancel) {
            onCancel(cancelledData);
          }
        }
        // Check if payment is still processing
        else if (verifyData.data && verifyData.data.status === 'processing') {
          console.log('⏳ Payment is still processing');
          const processingData = {
            ...verifyData,
            paymentStatus: 'processing',
            orderStatus: 'processing'
          };
          if (onSuccess) {
            onSuccess(processingData);
          }
        }
        // Check if payment failed (user left without payment)
        else if (verifyData.data && verifyData.data.status === 'failed') {
          console.log('❌ Payment failed (user left without payment)');
          const failedData = {
            ...verifyData,
            paymentStatus: 'failed',
            orderStatus: 'failed'
          };
          if (onError) {
            onError(failedData);
          }
        }
        // Payment failed
        else {
          console.log('❌ Payment verification failed');
          const failedData = {
            ...verifyData,
            paymentStatus: 'failed',
            orderStatus: 'failed'
          };
          if (onError) {
            onError(failedData);
          }
        }
      }

    } catch (error) {
      console.error('❌ Cashfree payment error:', error);
      setError(error.message || 'Failed to initiate payment');
      if (onError) {
        // Ensure all errors are marked as failed
        const errorData = {
          ...error,
          paymentStatus: 'failed',
          orderStatus: 'failed',
          message: error.message || 'Payment failed due to technical error'
        };
        onError(errorData);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="text-center mb-6">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900/20 mb-4">
          <CreditCard className="h-6 w-6 text-orange-600 dark:text-orange-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Secure Payment with Cashfree
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Complete your payment securely with Cashfree payment gateway
        </p>
      </div>

      {/* Payment Details */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Amount:</span>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatAmount(amount)}
          </span>
        </div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Order ID:</span>
          <span className="text-sm text-gray-900 dark:text-white">
            {orderId}
          </span>
        </div>
        {customerInfo && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">Paying as:</span>
            <span className="text-sm text-gray-900 dark:text-white">
              {customerInfo.name}
            </span>
          </div>
        )}
      </div>

      {/* Security Features */}
      <div className="flex items-center justify-center space-x-4 mb-6 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center">
          <Shield className="h-4 w-4 mr-1" />
          <span>SSL Secured</span>
        </div>
        <div className="flex items-center">
          <Lock className="h-4 w-4 mr-1" />
          <span>256-bit Encryption</span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Payment Button */}
      <button
        onClick={handlePayment}
        disabled={loading || !cashfree}
        className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-colors cashfree-payment-button ${
          loading || !cashfree
            ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            : 'bg-orange-600 hover:bg-orange-700 text-white'
        }`}
      >
        {loading ? (
          <>
            <Loader className="h-5 w-5 mr-2 animate-spin" />
            Processing Payment...
          </>
        ) : !cashfree ? (
          <>
            <Loader className="h-5 w-5 mr-2 animate-spin" />
            Initializing Payment...
          </>
        ) : (
          <>
            <CreditCard className="h-5 w-5 mr-2" />
            Pay with Cashfree
          </>
        )}
      </button>

      {/* Test Mode Notice */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
        <p className="text-xs text-blue-800 dark:text-blue-400 text-center">
          🧪 Test Mode: Use test cards or UPI for payment testing
        </p>
      </div>
    </div>
  );
};

export default CashfreePayment; 